import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { RecaptchaVerifier, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth } from '../config/firebase';
import Swal from 'sweetalert2';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [authConfig, setAuthConfig] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('phone'); // Default method
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [domainConfig, setDomainConfig] = useState(null);
  const { signIn, user, showUserForm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const phoneInputRef = useRef(null);
  const phoneInstanceRef = useRef(null);
  const hasRedirected = useRef(false);
  const injectedScriptRefs = useRef([]);

  useEffect(() => {
    loadAuthConfig();
  }, []);

  useEffect(() => {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    fetch(`/api/domain-config?host=${encodeURIComponent(host)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setDomainConfig(data);
          const root = document.documentElement.style;
          if (data.colors) {
            if (data.colors.primary) root.setProperty('--primary-color', data.colors.primary);
            if (data.colors.primaryHover) root.setProperty('--primary-hover', data.colors.primaryHover);
            if (data.colors.background) root.setProperty('--background', data.colors.background);
            if (data.colors.surface) root.setProperty('--surface', data.colors.surface);
            if (data.colors.textPrimary) root.setProperty('--text-primary', data.colors.textPrimary);
            if (data.colors.textSecondary) root.setProperty('--text-secondary', data.colors.textSecondary);
          }
          const snippets = Array.isArray(data.snippets) ? data.snippets : [];
          snippets.forEach(s => {
            if (s.tag !== 'script' || !s.src) return;
            const el = document.createElement('script');
            el.src = s.src;
            if (s.async) el.async = true;
            if (s.defer) el.defer = true;
            if (s.type) el.type = s.type;
            if (s['data-id']) el.setAttribute('data-id', s['data-id']);
            document.head.appendChild(el);
            injectedScriptRefs.current.push(el);
          });
        }
      })
      .catch(() => {});
    return () => {
      injectedScriptRefs.current.forEach(el => { try { el.remove(); } catch (_) {} });
      injectedScriptRefs.current = [];
    };
  }, []);

  // Handle email link authentication
  useEffect(() => {
    const handleEmailLink = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        console.log('🔗 Email link detected, processing authentication...');
        
        // Try to get email from localStorage first (most reliable)
        let email = window.localStorage.getItem('emailForSignIn');
        
        if (!email) {
          console.log('📧 Email not found in localStorage, checking URL parameters...');
          
          // Try to extract email from URL parameters
          const urlParams = new URLSearchParams(window.location.search);
          email = urlParams.get('email') || urlParams.get('oobCode');
          
          // Also check the hash fragment for Firebase parameters
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          if (!email) {
            email = hashParams.get('email') || hashParams.get('oobCode');
          }
          
          if (!email) {
            console.log('📧 Email not found in URL, checking if we can proceed without it...');
            
            // Try to sign in without email first (Firebase might have it internally)
            try {
              const result = await signInWithEmailLink(auth, '', window.location.href);
              console.log('✅ Email authentication successful without explicit email');
              
              // Clear the email from localStorage
              window.localStorage.removeItem('emailForSignIn');
              
              // Get the user token
              const token = await result.user.getIdToken();
              
              // Sign in to our backend
              await signIn(token);
              
              // Show success message
              await Swal.fire({
                icon: 'success',
                title: '¡Autenticación exitosa!',
                text: 'Has iniciado sesión correctamente con tu email.',
                confirmButtonColor: '#667eea',
                confirmButtonText: 'Continuar'
              });
              
              return; // Success, exit early
              
            } catch (error) {
              console.log('❌ Could not authenticate without email, will prompt user...');
              
              // Only show popup if we really can't find the email
              const { value: userEmail } = await Swal.fire({
                title: 'Introduce tu email',
                input: 'email',
                inputLabel: 'Email',
                inputPlaceholder: 'tu@email.com',
                showCancelButton: true,
                confirmButtonColor: '#667eea',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Continuar',
                cancelButtonText: 'Cancelar',
                inputValidator: (value) => {
                  if (!value || !value.includes('@')) {
                    return 'Por favor, introduce un email válido';
                  }
                }
              });

              if (!userEmail) {
                console.log('❌ User cancelled email input');
                return;
              }
              email = userEmail;
            }
          }
        }

        console.log('📧 Using email for authentication:', email);
        setLoading(true);
        setError('');

        try {
          // Sign in with email link
          const result = await signInWithEmailLink(auth, email, window.location.href);
          
          // Clear the email from localStorage
          window.localStorage.removeItem('emailForSignIn');
          
          console.log('✅ Email authentication successful');
          
          // Get the user token
          const token = await result.user.getIdToken();
          
          // Sign in to our backend
          await signIn(token);
          
          // Show success message
          await Swal.fire({
            icon: 'success',
            title: '¡Autenticación exitosa!',
            text: 'Has iniciado sesión correctamente con tu email.',
            confirmButtonColor: '#667eea',
            confirmButtonText: 'Continuar'
          });

        } catch (error) {
          console.error('Error signing in with email link:', error);
          
          let errorMessage = 'Error al autenticarse con el enlace. Por favor, intenta de nuevo.';
          
          if (error.code === 'auth/invalid-email') {
            errorMessage = 'El email introducido no es válido.';
          } else if (error.code === 'auth/invalid-action-code') {
            errorMessage = 'El enlace ha expirado o no es válido. Solicita un nuevo enlace.';
          } else if (error.code === 'auth/expired-action-code') {
            errorMessage = 'El enlace ha expirado. Solicita un nuevo enlace.';
          }
          
          setError(errorMessage);
          
          // Clear the email from localStorage on error
          window.localStorage.removeItem('emailForSignIn');
        } finally {
          setLoading(false);
        }
      }
    };

    handleEmailLink();
  }, [signIn]);

  const loadAuthConfig = async () => {
    try {
      console.log('🔄 Loading auth config...');
      const response = await fetch('/api/admin/public-config');
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const config = await response.json();
        console.log('✅ Auth config loaded:', config);
        setAuthConfig(config);
        
        // Set default method (first enabled method)
        if (config.phoneAuth?.enabled) {
          console.log('📞 Setting phone as default method');
          setSelectedMethod('phone');
        } else if (config.emailLinkAuth?.enabled) {
          console.log('✉️ Setting email as default method');
          setSelectedMethod('email');
        } else if (config.googleAuth?.enabled) {
          console.log('🔗 Setting google as default method');
          setSelectedMethod('google');
        }
      } else {
        console.error('❌ Failed to load auth config:', response.status);
        // Fallback: create a default config with all methods enabled
        const fallbackConfig = {
          phoneAuth: { enabled: true },
          emailLinkAuth: { enabled: true },
          googleAuth: { enabled: true }
        };
        console.log('🔄 Using fallback config:', fallbackConfig);
        setAuthConfig(fallbackConfig);
        setSelectedMethod('phone');
      }
    } catch (error) {
      console.error('❌ Error loading auth config:', error);
      // Fallback to phone auth if config fails to load
      const fallbackConfig = {
        phoneAuth: { enabled: true },
        emailLinkAuth: { enabled: true },
        googleAuth: { enabled: true }
      };
      console.log('🔄 Using fallback config due to error:', fallbackConfig);
      setAuthConfig(fallbackConfig);
      setSelectedMethod('phone');
    }
  };

  useEffect(() => {
    if (!authConfig) return;
    const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
    if (isDevelopment && authConfig?.emailLinkAuth?.enabled) {
      console.log('⚠️ Email authentication may not work in development due to Firebase domain restrictions');
      console.log('💡 Consider using phone authentication for testing');
    }
  }, [authConfig]);

  useEffect(() => {
    // Log redirect URL detection for debugging
    const urlParams = new URLSearchParams(location.search);
    const redirectUri = urlParams.get('redirect_uri');
    const fromState = location.state?.from;
    
    console.log('🔍 URL Search params:', location.search);
    console.log('🔍 Redirect URI from URL:', redirectUri);
    console.log('🔍 From state:', fromState);
    console.log('🔍 Current location:', location.pathname + location.search);
    
    if (redirectUri && isValidRedirectUri(redirectUri)) {
      console.log('🔗 SSO redirect URL detected:', redirectUri);
    } else if (fromState) {
      console.log('📍 Internal redirect detected:', fromState);
    } else {
      console.log('🏠 No redirect URL detected, will use default');
    }
  }, [location]);

  useEffect(() => {
    // Wait for Firebase Auth to be ready
    const waitForFirebase = async () => {
      try {
        // Wait a bit for Firebase to initialize completely
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if auth is properly initialized
        if (auth && auth.currentUser !== undefined) {
          setFirebaseReady(true);
        } else {
          // Try again after a longer delay
          setTimeout(() => {
            if (auth) {
              setFirebaseReady(true);
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error waiting for Firebase:', error);
        setFirebaseReady(true); // Continue anyway
      }
    };

    waitForFirebase();
  }, []);

  useEffect(() => {
    // Create reCAPTCHA verifier only after Firebase is ready
    if (firebaseReady && !recaptchaVerifier) {
      try {
        console.log('Creating RecaptchaVerifier...');
        
        // Clear any existing reCAPTCHA
        const existingContainer = document.getElementById('recaptcha-container');
        if (existingContainer) {
          existingContainer.innerHTML = '';
        }
        
        const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: (response) => {
            console.log('reCAPTCHA solved:', response);
          },
          'expired-callback': () => {
            console.log('reCAPTCHA expired');
            setRecaptchaVerifier(null);
          }
        });
        
        setRecaptchaVerifier(verifier);
        console.log('RecaptchaVerifier created successfully');
      } catch (error) {
        console.error('Error creating RecaptchaVerifier:', error);
        setError('Error inicializando verificación. Por favor, recarga la página.');
      }
    }

    // Cleanup on unmount
    return () => {
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (error) {
          console.log('Error clearing reCAPTCHA:', error);
        }
      }
    };
  }, [firebaseReady, recaptchaVerifier]);

  useEffect(() => {
    if (phoneInputRef.current && !phoneInstanceRef.current && !confirmationResult) {
      const init = async () => {
        // Wait for utils script to load
        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js';
          script.onload = resolve;
          script.onerror = resolve; // Continue even if script fails
          document.body.appendChild(script);
        });

        await new Promise(resolve => setTimeout(resolve, 100));

        try {
          if (phoneInputRef.current) {
            const iti = intlTelInput(phoneInputRef.current, {
              preferredCountries: ['es'],
              initialCountry: 'es',
              separateDialCode: true,
              utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
              dropdownContainer: document.body,
              nationalMode: false,
              formatOnDisplay: false,
              autoPlaceholder: 'aggressive',
              customPlaceholder: function() {
                return "666 777 888";
              }
            });

            phoneInstanceRef.current = iti;

            // Handle country change
            phoneInputRef.current.addEventListener('countrychange', function() {
              const currentValue = phoneInputRef.current.value;
              const cleanNumber = currentValue.replace(/\d/g, '');
              phoneInputRef.current.value = cleanNumber;
              
              setTimeout(() => {
                const event = new Event('input');
                phoneInputRef.current.dispatchEvent(event);
              }, 0);
            });
          }
        } catch (error) {
          console.error('Error initializing phone input:', error);
        }
      };

      init();

      return () => {
        if (phoneInstanceRef.current) {
          phoneInstanceRef.current.destroy();
          phoneInstanceRef.current = null;
        }
      };
    }
  }, [confirmationResult]);

  // Validate redirect_uri: same base domain as current auth host (e.g. auth.bilbo.news allows *.bilbo.news)
  const isValidRedirectUri = (uri) => {
    try {
      const url = new URL(uri);
      const hostname = url.hostname.toLowerCase();
      const currentHost = window.location.hostname.toLowerCase();
      if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
      const getBase = (h) => {
        const parts = h.split('.');
        return parts.length >= 2 ? parts.slice(-2).join('.') : null;
      };
      const authBase = getBase(currentHost);
      const redirectBase = getBase(hostname);
      return !!authBase && authBase === redirectBase;
    } catch {
      return false;
    }
  };

  // Redirect if user is already authenticated
  useEffect(() => {
    console.log('🔄 Login useEffect triggered');
    console.log('👤 User exists:', !!user);
    console.log('⏳ Loading state:', loading);
    console.log('📝 Show user form:', showUserForm);
    console.log('📊 User data exists:', !!user?.userData);
    console.log('✅ Profile complete:', user?.userData?.hasCompletedProfile);
    console.log('🔄 Has already redirected:', hasRedirected.current);
    console.log('🔍 Current URL params:', location.search);
    
    // Only proceed if user exists and loading is complete
    if (!user || loading) {
      console.log('⏸️ Not redirecting yet - user:', !!user, 'loading:', loading);
      return;
    }
    
    // Prevent multiple redirects
    if (hasRedirected.current) {
      console.log('🔄 Already redirected, skipping');
      return;
    }
    
    // Get redirect URL directly from URL params to avoid race condition
    const urlParams = new URLSearchParams(location.search);
    const redirectUri = urlParams.get('redirect_uri');
    
    console.log('🔄 Redirect effect triggered - user:', !!user);
    console.log('🔍 Direct redirect URI from URL:', redirectUri);
    console.log('📊 User data loaded:', !!user.userData);
    console.log('📝 Show user form:', showUserForm);
    console.log('📋 User data details:', {
      hasUserData: !!user?.userData,
      hasCompletedProfile: user?.userData?.hasCompletedProfile,
      name: user?.userData?.name,
      email: user?.userData?.email
    });
    
    let targetRedirect = '/dashboard';
    
    if (redirectUri && isValidRedirectUri(redirectUri)) {
      targetRedirect = redirectUri;
      console.log('🔗 Using SSO redirect URL:', targetRedirect);
    } else {
      console.log('🏠 Using default redirect to dashboard');
    }
    
    console.log('🚀 User authenticated, checking redirect flow...');
    console.log('📍 Current location:', window.location.href);
    console.log('🔗 Target redirect type:', typeof targetRedirect);
    console.log('🔗 Is external URL:', targetRedirect.startsWith('http'));
    
    // Check if user has completed profile directly
    const hasCompletedProfile = user?.userData?.hasCompletedProfile;
    const needsToCompleteProfile = showUserForm || !user?.userData || !hasCompletedProfile;
    
    console.log('🎯 Profile completion check:');
    console.log('  - hasCompletedProfile:', hasCompletedProfile);
    console.log('  - needsToCompleteProfile:', needsToCompleteProfile);
    console.log('  - showUserForm:', showUserForm);
    console.log('  - hasUserData:', !!user?.userData);
    console.log('  - NODE_ENV:', process.env.NODE_ENV);
    console.log('  - Current hostname:', window.location.hostname);
    
    // Mark as redirected to prevent multiple redirects
    hasRedirected.current = true;
    
    if (needsToCompleteProfile) {
      console.log('📝 User needs to complete profile first, redirecting to form');
      console.log('📍 Navigating to /complete-profile');
      navigate('/complete-profile', { replace: true });
    } else {
      console.log('✅ User profile complete, proceeding with redirect');
      console.log('🎯 Final target redirect:', targetRedirect);
      console.log('🔗 Is external URL:', targetRedirect.startsWith('http'));
      
      // Check if it's an external URL (SSO)
      if (targetRedirect.startsWith('http')) {
        console.log('🌐 Redirecting to external URL:', targetRedirect);
        console.log('📍 Current URL before redirect:', window.location.href);
        
        // Add a small delay to ensure logs are visible
        setTimeout(() => {
          console.log('🚀 Executing external redirect to:', targetRedirect);
          window.location.href = targetRedirect;
        }, 100);
      } else {
        console.log('📍 Redirecting to internal route:', targetRedirect);
        navigate(targetRedirect, { replace: true });
      }
    }
  }, [user, loading, navigate, location.search, showUserForm]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!termsAccepted || !privacyAccepted) {
      setError('Debes aceptar los términos y condiciones y la política de privacidad para continuar.');
      return;
    }
    
    setLoading(true);
    
    try {
      if (!phoneInputRef.current || !phoneInstanceRef.current) {
        throw new Error('Error al inicializar el input de teléfono');
      }

      const inputValue = phoneInputRef.current.value.trim();
      
      if (!inputValue) {
        setError('Por favor, introduce un número de teléfono');
        setLoading(false);
        return;
      }

      // Get phone number with country code
      const selectedCountryData = phoneInstanceRef.current.getSelectedCountryData();
      const dialCode = selectedCountryData.dialCode;
      const phoneNumber = `+${dialCode}${inputValue.replace(/\D/g, '')}`;
      
      console.log('Sending SMS to:', phoneNumber);

      // Ensure reCAPTCHA is ready
      let currentVerifier = recaptchaVerifier;
      if (!currentVerifier && firebaseReady) {
        try {
          console.log('Creating new RecaptchaVerifier for send code...');
          
          // Clear any existing reCAPTCHA
          const existingContainer = document.getElementById('recaptcha-container');
          if (existingContainer) {
            existingContainer.innerHTML = '';
          }
          
          currentVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible'
          });
          setRecaptchaVerifier(currentVerifier);
        } catch (verifierError) {
          console.error('Error creating new RecaptchaVerifier:', verifierError);
          throw new Error('Error inicializando verificación. Por favor, recarga la página.');
        }
      }

      if (!currentVerifier) {
        throw new Error('Verificación no disponible. Por favor, recarga la página.');
      }

      const result = await signIn(phoneNumber, currentVerifier);
      setConfirmationResult(result);
      sessionStorage.setItem('phoneNumber', phoneNumber);
      sessionStorage.setItem('consentsAcceptedAtLogin', JSON.stringify({
        termsAndConditions: { accepted: true, acceptedAt: new Date().toISOString() },
        privacyPolicy: { accepted: true, acceptedAt: new Date().toISOString() }
      }));
      
      Swal.fire({
        icon: 'success',
        title: 'Código Enviado',
        text: `Se ha enviado un código de verificación al número ${phoneNumber}`,
        timer: 3000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Error sending code:', error);
      
      let errorMessage = 'Error al enviar el código. Por favor, intenta de nuevo.';
      
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Número de teléfono inválido';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Demasiados intentos. Por favor, intenta más tarde';
      } else if (error.code === 'auth/captcha-check-failed') {
        errorMessage = 'Verificación reCAPTCHA fallida. Recarga la página e intenta de nuevo';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      
      // Reset reCAPTCHA if it failed
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
          setRecaptchaVerifier(null);
        } catch (resetError) {
          console.log('Error resetting reCAPTCHA:', resetError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!confirmationResult) {
      setError('Por favor, solicita un nuevo código de verificación');
      setLoading(false);
      return;
    }

    try {
      // Validate that the code has 6 digits
      if (!/^\d{6}$/.test(verificationCode)) {
        setError('El código debe tener 6 dígitos');
        return;
      }

      await confirmationResult.confirm(verificationCode);
      sessionStorage.removeItem('phoneNumber');
    } catch (err) {
      console.error('Error al verificar el código:', err);
      
      if (err.code === 'auth/invalid-verification-code') {
        setError('Código incorrecto. Por favor, verifica el código e intenta de nuevo.');
      } else if (err.code === 'auth/code-expired') {
        setError('El código ha expirado. Por favor, solicita uno nuevo.');
        setConfirmationResult(null);
      } else {
        setError('Error al verificar el código. Por favor, intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setConfirmationResult(null);
    setVerificationCode('');
    setError('');
    
    // Reset form to send new code
    const phoneNumber = sessionStorage.getItem('phoneNumber');
    if (phoneNumber && phoneInputRef.current) {
      // Extract just the number part for display
      const numberPart = phoneNumber.replace(/^\+\d+/, '');
      phoneInputRef.current.value = numberPart;
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setError('');
  };

  const handleGoogleSignIn = async () => {
    // TODO: Implement Google sign-in
    Swal.fire({
      icon: 'info',
      title: 'Función en desarrollo',
      text: 'La autenticación con Google estará disponible próximamente.',
      confirmButtonColor: '#667eea'
    });
  };

  const handleEmailSignIn = async () => {
    if (!email || !email.includes('@')) {
      setError('Por favor, introduce un email válido');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Check if we're in development mode
      const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
      
      // Configure the action code settings for localhost development
      const actionCodeSettings = {
        url: window.location.origin + '/login',
        handleCodeInApp: true,
        // Remove mobile app settings for localhost testing
        // iOS: {
        //   bundleId: 'com.yourapp.auth'
        // },
        // android: {
        //   packageName: 'com.yourapp.auth',
        //   installApp: true,
        //   minimumVersion: '12'
        // },
        // dynamicLinkDomain: 'rascaygana-pamplonews.page.link'
      };

      console.log('📧 Sending email link to:', email);
      console.log('🔗 Action code settings:', actionCodeSettings);
      console.log('🌍 Environment:', isDevelopment ? 'Development' : 'Production');

      // Send the email link
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      
      // Save the email for later use
      window.localStorage.setItem('emailForSignIn', email);
      
      // Show success message
      await Swal.fire({
        icon: 'success',
        title: '¡Enlace enviado!',
        text: `Hemos enviado un enlace de verificación a ${email}. Revisa tu bandeja de entrada y haz clic en el enlace para continuar.`,
        confirmButtonColor: '#667eea',
        confirmButtonText: 'Entendido'
      });

      // Clear the form
      setEmail('');
      
    } catch (error) {
      console.error('Error sending email link:', error);
      
      // Provide specific error messages based on the error code
      let errorMessage = 'Error al enviar el enlace. Por favor, intenta de nuevo.';
      
      if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet e intenta de nuevo.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'El email introducido no es válido.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Demasiados intentos. Espera un momento antes de intentar de nuevo.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'La autenticación por email no está habilitada en Firebase. Contacta con el administrador.';
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = 'Este dominio no está autorizado para enviar emails. En desarrollo, usa el método de teléfono.';
      }
      
      setError(errorMessage);
      
      // Show detailed error in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Detailed error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <LoginCard>
        <Header>
          {domainConfig?.logoUrl && (
            <LogoImage src={domainConfig.logoUrl} alt={domainConfig.name || ''} />
          )}
          <Title>{domainConfig?.name || 'Liga Estrella'}</Title>
          <Subtitle>Accede a tu cuenta</Subtitle>
        </Header>
        
        <Form onSubmit={!confirmationResult ? handleSendCode : handleVerifyCode}>
          <FormTitle>Iniciar Sesión</FormTitle>
          
          {/* Default Authentication Method */}
          {selectedMethod === 'phone' && !confirmationResult && (
            <>
              <FormGroup>
                <Label>Número de teléfono</Label>
                <PhoneInputContainer>
                  <Input
                    ref={phoneInputRef}
                    type="tel"
                    required
                    disabled={loading}
                  />
                </PhoneInputContainer>
                <HelpText>Introduce un número de móvil español (ej: 666777888)</HelpText>
              </FormGroup>
              
              <ConsentSection>
                <ConsentGroup $hasError={!termsAccepted && !!error}>
                  <ConsentCheckbox
                    type="checkbox"
                    id="login-terms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={loading}
                  />
                  <ConsentLabel htmlFor="login-terms">
                    Acepto los {domainConfig?.termsUrl ? (
                      <ConsentLink href={domainConfig.termsUrl} target="_blank" rel="noopener noreferrer">términos y condiciones</ConsentLink>
                    ) : (
                      <ConsentLink href="#" onClick={(e) => e.preventDefault()} aria-hidden>términos y condiciones</ConsentLink>
                    )} *
                  </ConsentLabel>
                </ConsentGroup>
                <ConsentGroup $hasError={!privacyAccepted && !!error}>
                  <ConsentCheckbox
                    type="checkbox"
                    id="login-privacy"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    disabled={loading}
                  />
                  <ConsentLabel htmlFor="login-privacy">
                    Acepto la {domainConfig?.privacyUrl ? (
                      <ConsentLink href={domainConfig.privacyUrl} target="_blank" rel="noopener noreferrer">política de privacidad</ConsentLink>
                    ) : (
                      <ConsentLink href="#" onClick={(e) => e.preventDefault()} aria-hidden>política de privacidad</ConsentLink>
                    )} *
                  </ConsentLabel>
                </ConsentGroup>
              </ConsentSection>
              
              <Button type="submit" disabled={loading}>
                {loading ? 'Enviando...' : 'Enviar código'}
              </Button>
            </>
          )}

          {selectedMethod === 'phone' && confirmationResult && (
            <>
              <SuccessMessage>
                📱 Código enviado al {sessionStorage.getItem('phoneNumber')}
              </SuccessMessage>
              
              <FormGroup>
                <Label>Código de verificación</Label>
                <VerificationInput
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  pattern="\d{6}"
                  maxLength="6"
                  required
                  disabled={loading}
                  autoFocus
                />
              </FormGroup>
              
              <Button type="submit" disabled={loading || verificationCode.length !== 6}>
                {loading ? 'Verificando...' : 'Verificar código'}
              </Button>
              
              <ResendButton type="button" onClick={handleResendCode} disabled={loading}>
                Solicitar nuevo código
              </ResendButton>
            </>
          )}

          {selectedMethod === 'email' && (
            <>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </FormGroup>
              
              <Button type="button" onClick={handleEmailSignIn} disabled={loading || !email}>
                {loading ? 'Enviando...' : 'Enviar enlace de verificación'}
              </Button>
            </>
          )}

          {selectedMethod === 'google' && (
            <>
              <GoogleButton type="button" onClick={handleGoogleSignIn} disabled={loading}>
                <GoogleIcon>🔗</GoogleIcon>
                Iniciar sesión con Google
              </GoogleButton>
            </>
          )}
          
          {error && <ErrorText>{error}</ErrorText>}
          <div id="recaptcha-container"></div>
        </Form>

        {/* Alternative Authentication Methods - solo si hay más de un método activo */}
        {authConfig && !confirmationResult && (() => {
          const enabledCount = [authConfig.phoneAuth?.enabled, authConfig.emailLinkAuth?.enabled, authConfig.googleAuth?.enabled].filter(Boolean).length;
          if (enabledCount < 2) return null;
          return (
          <AlternativeMethods>
            <Divider>
              <DividerText>o continúa con</DividerText>
            </Divider>
            
            <MethodButtons>
              {authConfig.phoneAuth?.enabled && selectedMethod !== 'phone' && (
                <MethodButton
                  type="button"
                  onClick={() => handleMethodSelect('phone')}
                  $active={selectedMethod === 'phone'}
                >
                  <MethodIcon>📞</MethodIcon>
                  Teléfono
                </MethodButton>
              )}
              
              {authConfig.emailLinkAuth?.enabled && selectedMethod !== 'email' && (
                <MethodButton
                  type="button"
                  onClick={() => handleMethodSelect('email')}
                  $active={selectedMethod === 'email'}
                >
                  <MethodIcon>✉️</MethodIcon>
                  Email
                </MethodButton>
              )}
              
              {authConfig.googleAuth?.enabled && selectedMethod !== 'google' && (
                <MethodButton
                  type="button"
                  onClick={() => handleMethodSelect('google')}
                  $active={selectedMethod === 'google'}
                >
                  <MethodIcon>🔗</MethodIcon>
                  Google
                </MethodButton>
              )}
            </MethodButtons>
          </AlternativeMethods>
          );
        })()}
      </LoginCard>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--background);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
`;

const LoginCard = styled.div`
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  padding: 3rem 2rem;
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 400px;
  border: 1px solid var(--border-light);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const LogoImage = styled.img`
  max-width: 120px;
  max-height: 56px;
  object-fit: contain;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  text-align: center;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid var(--border);
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: var(--transition);
  background: var(--surface);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const Button = styled.button`
  background: var(--primary-color);
  color: var(--text-light);
  padding: 1rem 2rem;
  border: none;
  border-radius: var(--border-radius);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  width: 100%;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: var(--danger);
  font-size: 0.85rem;
  margin-top: 0.25rem;
  text-align: center;
`;

const ConsentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ConsentGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  ${props => props.$hasError && 'outline: 1px solid var(--danger); border-radius: var(--border-radius); padding: 0.25rem;'}
`;

const ConsentCheckbox = styled.input`
  margin-top: 0.25rem;
  width: 1.1rem;
  height: 1.1rem;
  cursor: pointer;
  accent-color: var(--primary-color);
`;

const ConsentLabel = styled.label`
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  line-height: 1.4;
`;

const ConsentLink = styled.a`
  color: var(--primary-color);
  text-decoration: underline;
  &:hover {
    color: var(--primary-hover);
  }
`;

const SuccessMessage = styled.div`
  background: var(--success-light);
  color: var(--success-dark);
  padding: 1rem;
  border-radius: var(--border-radius);
  text-align: center;
  font-weight: 500;
  border: 1px solid var(--success);
`;

const HelpText = styled.small`
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-style: italic;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
  margin-top: 0.5rem;

  &:hover {
    color: var(--primary-hover);
  }
`;

const VerificationInput = styled(Input)`
  text-align: center;
  font-size: 1.2rem;
  letter-spacing: 0.5rem;
  font-weight: 600;
`;

const PhoneInputContainer = styled.div`
  position: relative;
`;

const AlternativeMethods = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-light);
  text-align: center;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border-light);
  }
`;

const DividerText = styled.span`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0 1rem;
  background: var(--surface);
  padding: 0 0.5rem;
`;

const MethodButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const MethodButton = styled.button`
  background: var(--surface);
  color: var(--text-primary);
  border: 2px solid var(--border);
  padding: 0.75rem 1rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 120px;
  flex: 1;
  max-width: 200px;

  &:hover:not(:disabled) {
    background: var(--surface-hover);
    border-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${(props) =>
    props.$active &&
    `
    background: var(--primary-color);
    color: var(--text-light);
    border-color: var(--primary-color);
    box-shadow: var(--shadow-lg);
  `}
`;

const MethodIcon = styled.span`
  font-size: 1.2rem;
`;

const GoogleButton = styled.button`
  background: #4285f4;
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;

  &:hover:not(:disabled) {
    background: #3367d6;
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GoogleIcon = styled.span`
  font-size: 1.2rem;
`;

export default Login;